import { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, X, Heart, ExternalLink, Lightbulb, Sun, Moon, Volume2, VolumeX, Globe, Download } from 'lucide-react';
import { usePostHog } from '@posthog/react';

const translations = {
  en: {
    title: "Elder Care Check",
    subtitle: "Protecting seniors from health misinformation",
    uploadPrompt: "Upload a screenshot or paste a health claim to verify",
    dropText: "Drop a screenshot or click to upload",
    dropSubtext: "Online posts, emails, articles — anything!",
    orPaste: "or paste text",
    placeholder: "Paste the health claim here...\n\nExample: 'Drinking silver water cures arthritis'",
    checkButton: "🔍 Check This Claim",
    checking: "Checking...",
    results: "Results",
    readAloud: "Read Aloud",
    stop: "Stop",
    checkAnother: "Check another →",
    insteadTry: "Instead, try this:",
    trustedSources: "Trusted sources:",
    summary: "Summary:",
    footer: "Always consult a healthcare professional for medical advice.",
    confidence: "confidence",
    installApp: "Install this app",
    installIOS: "Tap the Share button (⬆️) below and select \"Add to Home Screen\"",
    installOther: "Install this app for easy access! Tap your browser's menu and select \"Add to Home screen\" or \"Install\".",
    installButton: "Install App"
  },
  es: {
    title: "Control de Salud",
    subtitle: "Protegiendo a los mayores de la desinformación",
    uploadPrompt: "Sube una captura o pega un texto para verificar",
    dropText: "Arrastra una captura o haz clic para subir",
    dropSubtext: "Publicaciones en línea, correos, artículos — ¡cualquier cosa!",
    orPaste: "o pega texto",
    placeholder: "Pega la afirmación de salud aquí...\n\nEjemplo: 'Beber agua con plata cura la artritis'",
    checkButton: "🔍 Verificar",
    checking: "Verificando...",
    results: "Resultados",
    readAloud: "Leer en voz alta",
    stop: "Parar",
    checkAnother: "Verificar otro →",
    insteadTry: "En su lugar, intenta esto:",
    trustedSources: "Fuentes confiables:",
    summary: "Resumen:",
    footer: "Siempre consulte a un profesional de la salud.",
    confidence: "confianza",
    installApp: "Instalar esta aplicación",
    installIOS: "Toca el botón Compartir (⬆️) y selecciona \"Añadir a pantalla de inicio\"",
    installOther: "¡Instala esta aplicación para acceder fácilmente! Toca el menú de tu navegador y selecciona \"Añadir a pantalla de inicio\" o \"Instalar\".",
    installButton: "Instalar App"
  },
  zh: {
    title: "长者健康检查",
    subtitle: "保护老年人免受健康错误信息的侵害",
    uploadPrompt: "上传截图或粘贴健康声明以进行验证",
    dropText: "拖放截图或点击上传",
    dropSubtext: "网络帖子、电子邮件、文章——任何内容！",
    orPaste: "或粘贴文字",
    placeholder: "在此粘贴健康声明...\n\n例如：'喝银水可以治愈关节炎'",
    checkButton: "🔍 检查此声明",
    checking: "检查中...",
    results: "结果",
    readAloud: "朗读",
    stop: "停止",
    checkAnother: "检查另一个 →",
    insteadTry: "建议尝试：",
    trustedSources: "可信来源：",
    summary: "摘要：",
    footer: "请务必咨询医疗保健专业人员。",
    confidence: "置信度",
    installApp: "安装此应用",
    installIOS: "点击分享按钮（⬆️）并选择\"添加到主屏幕\"",
    installOther: "安装此应用以便快速访问！点击浏览器菜单并选择\"添加到主屏幕\"或\"安装\"。",
    installButton: "安装应用"
  },
  hi: {
    title: "बुज़ुर्ग स्वास्थ्य जाँच",
    subtitle: "बुज़ुर्गों को स्वास्थ्य गलत सूचना से बचाना",
    uploadPrompt: "स्क्रीनशॉट अपलोड करें या टेक्स्ट पेस्ट करें",
    dropText: "स्क्रीनशॉट यहाँ छोड़ें या क्लिक करें",
    dropSubtext: "ऑनलाइन पोस्ट, ईमेल, लेख - कुछ भी!",
    orPaste: "या टेक्स्ट पेस्ट करें",
    placeholder: "स्वास्थ्य दावा यहाँ पेस्ट करें...\n\nउदाहरण: 'चांदी का पानी पीने से गठिया ठीक हो जाता है'",
    checkButton: "🔍 जाँच करें",
    checking: "जाँच हो रही है...",
    results: "परिणाम",
    readAloud: "पढ़ कर सुनाएं",
    stop: "रोकें",
    checkAnother: "दूसरा जाँचें →",
    insteadTry: "इसके बजाय, यह आज़माएं:",
    trustedSources: "विश्वसनीय स्रोत:",
    summary: "सारांश:",
    footer: "कृपया हमेशा डॉक्टर से सलाह लें।",
    confidence: "विश्वास",
    installApp: "यह ऐप इंस्टॉल करें",
    installIOS: "शेयर बटन (⬆️) टैप करें और \"होम स्क्रीन में जोड़ें\" चुनें",
    installOther: "आसान पहुंच के लिए यह ऐप इंस्टॉल करें! अपने ब्राउज़र मेनू में \"होम स्क्रीन में जोड़ें\" या \"इंस्टॉल\" चुनें।",
    installButton: "ऐप इंस्टॉल करें"
  },
  ar: {
    title: "فحص صحة كبار السن",
    subtitle: "حماية كبار السن من المعلومات الصحية الخاطئة",
    uploadPrompt: "ارفع صورة أو الصق نصاً للتحقق",
    dropText: "اسحب صورة أو انقر للرفع",
    dropSubtext: "المنشورات عبر الإنترنت، الرسائل، المقالات - أي شيء!",
    orPaste: "أو الصق نصاً",
    placeholder: "الصق الادعاء الصحي هنا...\n\nمثال: 'شرب الماء الفضي يشفي التهاب المفاصل'",
    checkButton: "🔍 تحقق من هذا",
    checking: "جاري التحقق...",
    results: "النتائج",
    readAloud: "اقرأ بصوت عالٍ",
    stop: "توقف",
    checkAnother: "تحقق من آخر ←",
    insteadTry: "بدلاً من ذلك، جرّب:",
    trustedSources: "مصادر موثوقة:",
    summary: "ملخص:",
    footer: "استشر دائماً أخصائي الرعاية الصحية.",
    confidence: "الثقة",
    installApp: "تثبيت هذا التطبيق",
    installIOS: "اضغط على زر المشاركة (⬆️) واختر \"إضافة إلى الشاشة الرئيسية\"",
    installOther: "قم بتثبيت هذا التطبيق للوصول السهل! اضغط على قائمة المتصفح واختر \"إضافة إلى الشاشة الرئيسية\" أو \"تثبيت\".",
    installButton: "تثبيت التطبيق"
  },
  fr: {
    title: "Vérif Santé Aînés",
    subtitle: "Protéger les aînés contre la désinformation",
    uploadPrompt: "Téléchargez une capture ou collez un texte à vérifier",
    dropText: "Déposez une capture ou cliquez pour télécharger",
    dropSubtext: "Publications en ligne, courriels, articles — tout!",
    orPaste: "ou collez du texte",
    placeholder: "Collez l'affirmation de santé ici...\n\nExemple: 'Boire de l'eau argentée guérit l'arthrite'",
    checkButton: "🔍 Vérifier",
    checking: "Vérification...",
    results: "Résultats",
    readAloud: "Lire à voix haute",
    stop: "Arrêter",
    checkAnother: "Vérifier un autre →",
    insteadTry: "Essayez plutôt ceci:",
    trustedSources: "Sources fiables:",
    summary: "Résumé:",
    footer: "Consultez toujours un professionnel de santé.",
    confidence: "confiance",
    installApp: "Installer cette application",
    installIOS: "Appuyez sur le bouton Partager (⬆️) et sélectionnez \"Sur l'écran d'accueil\"",
    installOther: "Installez cette application pour un accès facile ! Appuyez sur le menu de votre navigateur et sélectionnez \"Ajouter à l'écran d'accueil\" ou \"Installer\".",
    installButton: "Installer l'app"
  }
};

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

export default function App() {
  //Analytics
  const posthog = usePostHog();

  const [textInput, setTextInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lang, setLang] = useState('en');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  const t = translations[lang];

  // Check if iOS and not installed
  useState(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    setIsIOS(isIOSDevice);
    
    // Show iOS prompt for Safari on iOS
    if (isIOSDevice && !isInStandaloneMode) {
      setShowIOSPrompt(true);
    }
    // Show generic install prompt for desktop browsers (except Safari)
    else if (!isInStandaloneMode && !isSafari && !isIOSDevice) {
      setShowIOSPrompt(true); // Reuse same banner with different text
    }
  }, []);

  // PWA Install Prompt (for Chrome/Android)
  useState(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  const analyzeContent = async () => {
    posthog.capture('claim_checked');
    
    setIsAnalyzing(true);
    setResult(null);
    stopSpeaking();
    
    try {
      let body = {};
      
      if (uploadedFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(uploadedFile);
        });
        body = { imageBase64: base64, lang };
      } else if (textInput) {
        body = { text: textInput, lang };
      }

      const response = await fetch('https://elder-care-check-9gqm.vercel.app/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      if (data.success) {
        setResult(data.result);
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Could not connect to server');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearFile = () => setUploadedFile(null);
  const clearAll = () => {
    setResult(null);
    setTextInput('');
    setUploadedFile(null);
    stopSpeaking();
  };

  const speakResults = () => {
    if (!result || !result.claims) return;
    stopSpeaking();
    
    let textToSpeak = "";
    result.claims.forEach((claim, i) => {
      textToSpeak += `${claim.text}. ${claim.verdict}. `;
      if (claim.evidence) textToSpeak += `${claim.evidence} `;
      if (claim.alternative) textToSpeak += `${claim.alternative} `;
    });
    if (result.summary) textToSpeak += result.summary;

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.rate = 0.85;
    
    // Set language for speech
    const langCodes = {
      en: 'en-US',
      es: 'es-ES',
      zh: 'zh-CN',
      hi: 'hi-IN',
      ar: 'ar-SA',
      fr: 'fr-FR'
    };
    utterance.lang = langCodes[lang] || 'en-US';
    
    // Load voices and try to find one for the language
    const setVoiceAndSpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      
      // Try to find exact language match
      let selectedVoice = voices.find(v => v.lang.startsWith(langCodes[lang]));
      
      // If not found, try broader match
      if (!selectedVoice) {
        selectedVoice = voices.find(v => v.lang.startsWith(lang));
      }
      
      // If still not found, try any voice that matches the language name
      if (!selectedVoice && lang === 'hi') {
        selectedVoice = voices.find(v => v.lang.includes('hi') || v.name.toLowerCase().includes('hindi'));
      }
      if (!selectedVoice && lang === 'ar') {
        selectedVoice = voices.find(v => v.lang.includes('ar') || v.name.toLowerCase().includes('arabic'));
      }
      if (!selectedVoice && lang === 'fr') {
        selectedVoice = voices.find(v => v.lang.includes('fr') || v.name.toLowerCase().includes('french'));
      }
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        console.log('Using voice:', selectedVoice.name, selectedVoice.lang);
      } else {
        console.log('No voice found for', lang, '- using default');
      }
      
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = (e) => {
        console.error('Speech error:', e);
        setIsSpeaking(false);
      };
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
    };
    
    // Make sure voices are loaded
    if (window.speechSynthesis.getVoices().length > 0) {
      setVoiceAndSpeak();
    } else {
      window.speechSynthesis.onvoiceschanged = setVoiceAndSpeak;
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const theme = {
    bg: darkMode ? 'bg-gradient-to-b from-blue-950 to-slate-950' : 'bg-gradient-to-b from-blue-50 to-slate-100',
    text: darkMode ? 'text-white' : 'text-slate-800',
    subtext: darkMode ? 'text-slate-400' : 'text-slate-600',
    card: darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300',
    input: darkMode ? 'bg-slate-900/80 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400',
    upload: darkMode ? 'border-blue-700 bg-blue-950/50 hover:border-blue-400' : 'border-blue-300 bg-blue-50 hover:border-blue-500',
    uploadText: darkMode ? 'text-blue-100' : 'text-blue-800',
    button: darkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700',
    evidence: darkMode ? 'bg-slate-800/50' : 'bg-slate-100',
    alternative: darkMode ? 'bg-green-900/30 border-green-700/50' : 'bg-green-50 border-green-300',
    summary: darkMode ? 'bg-blue-900/30 border-blue-700/50 text-blue-100' : 'bg-blue-50 border-blue-300 text-blue-800',
    dropdown: darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-300 shadow-lg',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto p-6">
        
        {/* iOS Install Prompt */}
        {showIOSPrompt && (
          <div className={`mb-4 rounded-xl p-4 border ${darkMode ? 'bg-blue-900/30 border-blue-700/50' : 'bg-blue-50 border-blue-300'}`}>
            <div className="flex items-start gap-3">
              <Download className="text-blue-500 mt-1" size={24} />
              <div className="flex-1">
                <p className={`font-semibold mb-1 ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  📲 {t.installApp}
                </p>
                {isIOS ? (
                  <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    {t.installIOS}
                  </p>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    {t.installOther}
                  </p>
                )}
              </div>
              <button 
                onClick={() => setShowIOSPrompt(false)}
                className={theme.subtext}
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
        
        {/* Header Controls */}
        <div className="flex justify-end gap-2 mb-2">
          {/* Install App Button */}
          {showInstallButton && (
            <button 
              onClick={handleInstallClick}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition ${
                darkMode ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <Download size={20} />
              {t.installButton}
            </button>
          )}
          
          {/* Language Selector */}
          <div className="relative">
            <button 
              onClick={() => setShowLangMenu(!showLangMenu)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 shadow'}`}
            >
              <Globe size={20} />
              <span>{languages.find(l => l.code === lang)?.flag}</span>
            </button>
            {showLangMenu && (
              <div className={`absolute right-0 mt-2 py-2 rounded-lg border z-50 ${theme.dropdown}`}>
                {languages.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setShowLangMenu(false); }}
                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-blue-600 hover:text-white ${lang === l.code ? 'bg-blue-600 text-white' : ''}`}
                  >
                    <span>{l.flag}</span>
                    <span>{l.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Dark/Light Toggle */}
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 rounded-lg ${darkMode ? 'bg-slate-800 text-yellow-400' : 'bg-white text-slate-700 shadow'}`}
          >
            {darkMode ? <Sun size={24} /> : <Moon size={24} />}
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="text-rose-400" size={36} />
            <h1 className="text-4xl font-bold">{t.title}</h1>
          </div>
          <p className={`text-lg ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>{t.subtitle}</p>
          <p className={`mt-2 ${theme.subtext}`}>{t.uploadPrompt}</p>
        </div>

        {/* Upload */}
        <label className="block mb-4">
          <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${theme.upload}`}>
            <Upload size={36} className={`mx-auto mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <p className={`text-lg ${theme.uploadText}`}>{t.dropText}</p>
            <p className={`mt-1 ${theme.subtext}`}>{t.dropSubtext}</p>
          </div>
          <input type="file" className="hidden" accept="image/*" onChange={(e) => setUploadedFile(e.target.files[0])} />
        </label>

        {uploadedFile && (
          <div className={`flex items-center gap-2 rounded-lg p-3 mb-4 ${darkMode ? 'bg-green-900/30 border border-green-500/50' : 'bg-green-50 border border-green-400'}`}>
            <CheckCircle className="text-green-500" size={20} />
            <span className={`flex-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{uploadedFile.name}</span>
            <button onClick={clearFile}><X size={20} /></button>
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-4 my-5">
          <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700' : 'bg-slate-300'}`}></div>
          <span className={theme.subtext}>{t.orPaste}</span>
          <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700' : 'bg-slate-300'}`}></div>
        </div>

        {/* Text Input */}
        <div className="mb-6">
          <div className={`flex items-start gap-3 border rounded-xl p-4 ${theme.input}`}>
            <FileText size={22} className={theme.subtext} />
            <textarea
              placeholder={t.placeholder}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={5}
              className="flex-1 bg-transparent outline-none resize-none text-lg"
            />
          </div>
        </div>

        {/* Button */}
        <button
          onClick={analyzeContent}
          disabled={(!textInput && !uploadedFile) || isAnalyzing}
          className={`w-full py-4 ${theme.button} disabled:bg-slate-500 rounded-xl font-semibold text-lg text-white transition flex items-center justify-center gap-2`}
        >
          {isAnalyzing ? <><Loader2 className="animate-spin" size={24} /> {t.checking}</> : t.checkButton}
        </button>

        {/* Results */}
        {result && result.claims && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-2xl font-semibold">{t.results}</h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={isSpeaking ? stopSpeaking : speakResults}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${isSpeaking ? 'bg-rose-600 text-white' : darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-700'}`}
                >
                  {isSpeaking ? <><VolumeX size={18} /> {t.stop}</> : <><Volume2 size={18} /> {t.readAloud}</>}
                </button>
                <button onClick={clearAll} className={`${theme.subtext} hover:underline text-sm`}>{t.checkAnother}</button>
              </div>
            </div>
            
            {result.claims.map((claim, i) => (
              <div key={i} className={`border rounded-xl p-5 ${theme.card}`}>
                <p className={`text-lg mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>"{claim.text}"</p>
                
                <div className="flex items-center gap-2 mb-4">
  {/* Green checkmark for TRUE only */}
  {(
    (claim.verdict === 'TRUE') ||
    (claim.verdict === 'VERDADERO') ||
    (claim.verdict === '正确') ||
    (claim.verdict === 'सत्य') ||
    (claim.verdict === 'صحيح') ||
    (claim.verdict === 'VRAI')
  ) && <CheckCircle className="text-green-400" size={24} />}
  
  {/* Red warning for FALSE */}
  {(
    (claim.verdict === 'FALSE') ||
    (claim.verdict === 'FALSO') ||
    (claim.verdict === '错误') ||
    (claim.verdict === 'असत्य') ||
    (claim.verdict === 'خاطئ') ||
    (claim.verdict === 'FAUX')
  ) && <AlertTriangle className="text-red-400" size={24} />}
  
  {/* Yellow warning for PARTIALLY TRUE */}
  {(
    (claim.verdict.includes('PARTIALLY')) ||
    (claim.verdict.includes('PARCIALMENTE')) ||
    (claim.verdict.includes('部分')) ||
    (claim.verdict.includes('आंशिक')) ||
    (claim.verdict.includes('جزئياً')) ||
    (claim.verdict.includes('PARTIELLEMENT'))
  ) && <AlertTriangle className="text-yellow-400" size={24} />}
  
  {/* Gray warning for UNVERIFIABLE */}
  {(
    (claim.verdict.includes('UNVERIFIABLE')) ||
    (claim.verdict.includes('NO VERIFICABLE')) ||
    (claim.verdict.includes('无法验证')) ||
    (claim.verdict.includes('सत्यापित नहीं')) ||
    (claim.verdict.includes('غير قابل')) ||
    (claim.verdict.includes('INVÉRIFIABLE'))
  ) && <AlertTriangle className="text-slate-400" size={24} />}
  
  <span className={`font-bold text-xl ${
    (claim.verdict === 'TRUE' || claim.verdict === 'VERDADERO' || claim.verdict === '正确' || claim.verdict === 'सत्य' || claim.verdict === 'صحيح' || claim.verdict === 'VRAI') ? 'text-green-400' : 
    (claim.verdict === 'FALSE' || claim.verdict === 'FALSO' || claim.verdict === '错误' || claim.verdict === 'असत्य' || claim.verdict === 'خاطئ' || claim.verdict === 'FAUX') ? 'text-red-400' : 
    (claim.verdict.includes('PARTIALLY') || claim.verdict.includes('PARCIALMENTE') || claim.verdict.includes('部分') || claim.verdict.includes('आंशिक') || claim.verdict.includes('جزئياً') || claim.verdict.includes('PARTIELLEMENT')) ? 'text-yellow-400' : 'text-slate-400'
  }`}>{claim.verdict}</span>
  {claim.confidence && <span className={theme.subtext}>({claim.confidence}% {t.confidence})</span>}
</div>

                {claim.evidence && (
                  <div className={`rounded-lg p-4 mb-4 ${theme.evidence}`}>
                    <p className={darkMode ? 'text-slate-300' : 'text-slate-600'}>{claim.evidence}</p>
                  </div>
                )}

                {claim.alternative && (
                  <div className={`border rounded-lg p-4 mb-4 ${theme.alternative}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="text-green-500" size={20} />
                      <span className="text-green-500 font-semibold">{t.insteadTry}</span>
                    </div>
                    <p className={darkMode ? 'text-green-100' : 'text-green-800'}>{claim.alternative}</p>
                  </div>
                )}

                {claim.sources && claim.sources.length > 0 && (
                  <div className="mt-3">
                    <p className={`text-sm mb-2 ${theme.subtext}`}>{t.trustedSources}</p>
                    <div className="flex flex-wrap gap-2">
                      {claim.sources.map((source, j) => (
                        <a key={j} href={source.url} target="_blank" rel="noopener noreferrer"
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          <ExternalLink size={14} />{source.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {result.summary && (
              <div className={`border rounded-xl p-5 ${theme.summary}`}>
                <p className="text-lg"><strong>{t.summary}</strong> {result.summary}</p>
              </div>
            )}
          </div>
        )}

        {result && result.raw && (
          <div className={`mt-8 border rounded-xl p-5 ${theme.card}`}>
            <p>{result.raw}</p>
          </div>
        )}

        <div className={`mt-12 text-center ${theme.subtext}`}>
          <p className="text-sm mb-4">{t.footer}</p>
  
          {/* Product Hunt Badge */}
            <a 
              href="https://www.producthunt.com/products/elder-care-check?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-elder&#0045;care&#0045;check" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img 
                src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1042776&theme=${darkMode ? 'dark' : 'light'}&t=1764394683500`}
                alt="Elder Care Check - Seniors verify online health claims using AI | Product Hunt" 
                style={{ width: '250px', height: '54px' }} 
                width="250" 
                height="54" 
              />
            </a>
        </div>
      </div>
    </div>
  );
}